import { useEffect, useMemo, useRef, useState } from 'react';
import { Bell, CheckCircle, CheckCheck, Check } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { cn } from '../lib/cn';
import type {
  AppNotification,
  NotificationCategory,
  NotificationSeverity,
} from '../mocks/clientBatch';
import { useMyNotifications, markNotificationRead, markAllNotificationsRead } from '../api/platform';

type FilterKey = 'all' | 'billing' | 'system' | 'security' | 'team';

const FILTERS: Array<{ id: FilterKey; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'billing', label: 'Billing' },
  { id: 'system', label: 'System' },
  { id: 'security', label: 'Security' },
  { id: 'team', label: 'Team' },
];

const SEVERITY_DOT: Record<NotificationSeverity, string> = {
  info: 'bg-sky-500 dark:bg-sky-400',
  warning: 'bg-amber-500 dark:bg-amber-400',
  critical: 'bg-rose-500 dark:bg-rose-400',
};

/**
 * Bell icon + slide-down panel. Reads `myNotifications` from clientBatch and
 * tracks read/dismissed state locally so we don't mutate the import.
 */
export function NotificationsBell() {
  const { data: myNotifications } = useMyNotifications();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<FilterKey>('all');
  const ref = useRef<HTMLDivElement>(null);

  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  // Seed the read/dismissed sets once notifications land. Keyed on the raw
  // query data (stable `undefined` until loaded) so it runs once, not a loop.
  useEffect(() => {
    if (!myNotifications) return;
    setReadIds(new Set(myNotifications.filter((n) => n.readAt).map((n) => n.id)));
    setDismissedIds(new Set(myNotifications.filter((n) => n.dismissedAt).map((n) => n.id)));
  }, [myNotifications]);

  const allItems = useMemo<AppNotification[]>(
    () => (myNotifications ?? []).filter((n) => !dismissedIds.has(n.id)),
    [myNotifications, dismissedIds],
  );

  const unreadCount = useMemo(
    () => allItems.filter((n) => !readIds.has(n.id)).length,
    [allItems, readIds],
  );

  const filtered = useMemo(() => {
    if (filter === 'all') return allItems;
    return allItems.filter((n) => filterMatches(n.category, filter));
  }, [allItems, filter]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClick);
      document.addEventListener('keydown', onKey);
      return () => {
        document.removeEventListener('mousedown', onClick);
        document.removeEventListener('keydown', onKey);
      };
    }
    return undefined;
  }, [open]);

  function markRead(id: string) {
    setReadIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    // Persist — optimistic local state above keeps the panel instant.
    void markNotificationRead(id).catch(() => undefined);
  }

  function markAllRead() {
    setReadIds(new Set(allItems.map((n) => n.id)));
    void markAllNotificationsRead().catch(() => undefined);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Notifications${unreadCount > 0 ? ` — ${unreadCount} unread` : ''}`}
        className="w-9 h-9 grid place-items-center rounded-md text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition relative"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold grid place-items-center ring-2 ring-white dark:ring-zinc-950 tabular-nums"
            aria-hidden
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            'absolute right-0 mt-1.5 z-50 w-[22rem] max-w-[calc(100vw-1.5rem)]',
            'rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-600 shadow-lg',
            'overflow-hidden animate-in fade-in slide-in-from-top-1',
          )}
        >
          <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-700 flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">Notifications</span>
            <button
              type="button"
              onClick={markAllRead}
              disabled={unreadCount === 0}
              className="inline-flex items-center gap-1 text-xs font-medium text-brand dark:text-brand-dm hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
            >
              <CheckCheck className="w-3.5 h-3.5" /> All
            </button>
          </div>

          <div className="px-2 py-2 border-b border-zinc-200 dark:border-zinc-700 flex gap-1 overflow-x-auto nice-scroll">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={cn(
                  'px-2.5 py-1 rounded-md text-xs font-medium transition whitespace-nowrap',
                  filter === f.id
                    ? 'bg-zinc-900 text-white dark:bg-zinc-50 dark:text-zinc-900'
                    : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800/70',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="max-h-96 overflow-y-auto nice-scroll divide-y divide-zinc-200 dark:divide-zinc-700">
            {filtered.length === 0 ? (
              <div className="py-10 px-4 text-center">
                <CheckCircle className="w-6 h-6 text-emerald-500 dark:text-emerald-400 mx-auto mb-2" />
                <p className="text-sm text-zinc-600 dark:text-zinc-300">You're all caught up.</p>
              </div>
            ) : (
              filtered.map((n) => {
                const isUnread = !readIds.has(n.id);
                return (
                  <NotifRow
                    key={n.id}
                    n={n}
                    unread={isUnread}
                    onMarkRead={() => markRead(n.id)}
                    onClick={() => {
                      markRead(n.id);
                      setOpen(false);
                    }}
                  />
                );
              })
            )}
          </div>

          <div className="px-4 py-2 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-between">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="text-xs font-medium text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50"
            >
              View all →
            </Link>
            <span className="text-[11px] text-zinc-400 dark:text-zinc-500 tabular-nums">
              {filtered.length} item{filtered.length === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function filterMatches(cat: NotificationCategory, f: FilterKey): boolean {
  if (f === 'all') return true;
  if (f === 'billing') return cat === 'billing' || cat === 'commission';
  if (f === 'system') return cat === 'system' || cat === 'application';
  if (f === 'security') return cat === 'security';
  if (f === 'team') return cat === 'team';
  return true;
}

function NotifRow({
  n,
  unread,
  onClick,
  onMarkRead,
}: {
  n: AppNotification;
  unread: boolean;
  onClick: () => void;
  onMarkRead: () => void;
}) {
  const inner = (
    <div
      className={cn(
        'group relative flex items-start gap-3 px-4 py-3 transition',
        unread
          ? 'bg-brand-subtle/30 hover:bg-brand-subtle/50 dark:bg-brand-dm-subtle/10 dark:hover:bg-zinc-800/70'
          : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/70',
      )}
    >
      {unread && (
        <span
          aria-hidden
          className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-brand dark:bg-brand-dm"
        />
      )}
      <span
        aria-hidden
        className={cn('mt-1.5 w-2 h-2 rounded-full flex-shrink-0', SEVERITY_DOT[n.severity])}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <span
            className={cn(
              'text-zinc-950 dark:text-zinc-50 line-clamp-2',
              unread ? 'font-semibold text-sm' : 'font-medium text-sm',
            )}
          >
            {n.title}
          </span>
          <span className="text-[11px] text-zinc-500 dark:text-zinc-400 tabular-nums flex-shrink-0 mt-0.5">
            {n.createdAt}
          </span>
        </div>
        <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5 line-clamp-2">{n.body}</p>
      </div>
      {unread && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onMarkRead();
          }}
          aria-label="Mark as read"
          className="absolute top-2 right-2 w-6 h-6 grid place-items-center rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 opacity-0 group-hover:opacity-100 transition"
        >
          <Check className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  if (n.actionUrl && n.actionUrl.startsWith('/')) {
    return (
      <Link to={n.actionUrl as never} onClick={onClick} className="block">
        {inner}
      </Link>
    );
  }
  if (n.actionUrl) {
    return (
      <a
        href={n.actionUrl}
        target="_blank"
        rel="noreferrer"
        onClick={onClick}
        className="block"
      >
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className="block w-full text-left">
      {inner}
    </button>
  );
}
